<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { NIcon, type DataTableColumns, type DropdownOption } from 'naive-ui';
import {
  IconCheckCircle,
  IconCheckCircleFilled,
  IconDocumentText,
  IconEye,
  IconFilter,
  IconMore,
  IconTags,
} from '@/components/design/icons';
import {
  PreprocessingTaskStatus,
  type ReviewProjectFileInDb,
  type ReviewProjectFilePageViewInDb,
  type ReviewProjectFileWithRelations,
} from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterPanel, {
  type FilterConfig,
} from '@/components/design/composite/GaiaFilterPanel.vue';
import GaiaFileRow from '@/components/design/review/GaiaFileRow.vue';
import { gaiaStatus, gaiaTextButtonOverrides } from '@/components/design/themes/tokens';
import AutoTagModal from '@/components/reviewProject/AutoTagModal.vue';
import ReviewFileTagsCell from '@/components/reviewProject/ReviewFileTagsCell.vue';
import UserInitialsAvatar from '@/components/UserInitialsAvatar.vue';
import { useReviewProjectFileWebSocket } from '@/composables/useReviewProjectFileWebSocket';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { setVaultFileDragData, type VaultFileDragEntry } from '@/utils/vaultFileDrag';
import { logger } from '@/utils/logger';
import { safeStringify } from '@/utils/jsonHelpers';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * The data room's file list (Review Projects mock). A list rather than the data
 * table it replaces: the workspace gives the data room a 344px rail, which no
 * seven-column table fits — so each file is one three-line row (name, vault
 * path, tags) and everything the columns used to carry (version, seen
 * progress, preview, notes) moved into the row's trailing controls and its ⋯
 * menu.
 */
export type ReviewFilesMetaKey =
  'name' | 'version' | 'path' | 'tags' | 'seen' | 'reviewed' | 'actions';
export type ReviewFilesActionKey = 'preview' | 'notes';

const props = withDefaults(
  defineProps<{
    projectUid: string;
    /** Predetermined filter: the list only ever shows usable or unusable files. */
    usable: boolean;
    /** Which of a file's details the row carries. */
    columns?: ReviewFilesMetaKey[];
    /** Row actions offered in the ⋯ menu, in menu order. */
    actions?: ReviewFilesActionKey[];
    /** Rows can be dragged out (vault-file drag payload, e.g. onto an analysis). */
    draggableRows?: boolean;
    /** Show the "Tag all / Tag N files" auto-tag control (usable files only). */
    enableAutoTag?: boolean;
  }>(),
  {
    columns: () => ['name', 'version', 'path', 'tags', 'seen'],
    actions: () => ['preview'],
  }
);

/** The rail's collapsed stub shows the count, which only this component knows. */
const emit = defineEmits<{ 'update:count': [count: number] }>();

const message = useGaiaToast();
const reviewProjectStore = useReviewProjectStore();

// Search/filters/page live in the store so they survive the data room being
// collapsed, the view being switched and the pane being remounted.
const tableState = computed(
  () => reviewProjectStore.dataRoomFilesTableState[props.usable ? 'usable' : 'unusable']
);

const files = ref<ReviewProjectFileWithRelations[]>([]);
const totalCount = ref(0);
const loading = ref(false);
const filterOptions = ref<Record<string, (string | null)[]>>({});
const showFilterPanel = ref(Object.keys(tableState.value.activeFilters).length > 0);
const hasActiveFilters = computed(() => Object.keys(tableState.value.activeFilters).length > 0);

const shows = computed(() => new Set(props.columns));

// Auto-tag: translate the shared revision-uid selection into review-project file uids.
const selectedFileUids = computed<string[]>(() =>
  reviewProjectStore.dataRoomSelectedRevisionUids
    .map(uid => reviewProjectStore.dataRoomFileUidByRevisionUid[uid])
    .filter((uid): uid is string => Boolean(uid))
);
const autoTagButtonLabel = computed(() =>
  selectedFileUids.value.length ? `Tag ${selectedFileUids.value.length} files` : 'Tag all files'
);
const showAutoTagModal = ref(false);

function onAutoTagCreated(): void {
  // Force the Tasks page to refetch so the new run shows up there.
  reviewProjectStore.taskGroups = null;
  reviewProjectStore.dataRoomSelectedRevisionUids = [];
}

const FILTER_LABELS: Record<string, string> = {
  tags: 'Tags',
  mime_type: 'Type',
};

const filterConfig = computed((): FilterConfig[] =>
  Object.keys(filterOptions.value).map(key => ({
    key,
    label: FILTER_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  }))
);

// GaiaJsonValue is unknown; FilterBar wants string | null ('true'/'false' render as Yes/No).
// The usable option is dropped — it is predetermined per view.
function sanitizeFilterOptions(options: Record<string, unknown[]>): void {
  const sanitized: Record<string, (string | null)[]> = {};
  for (const [key, values] of Object.entries(options)) {
    if (key === 'usable') continue;
    sanitized[key] = values.map(v => (v === null ? null : safeStringify(v)));
  }
  filterOptions.value = sanitized;
}

function buildColumnFilters(): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(tableState.value.activeFilters)) {
    if (Array.isArray(value) && value.length > 0) {
      filters[key] = value.map(v => (v === '(Empty)' ? '' : String(v)));
    }
  }
  return filters;
}

async function fetchFiles(): Promise<void> {
  loading.value = true;
  try {
    const result = await ReviewProjectService.searchFiles(props.projectUid, {
      text: tableState.value.search,
      page: tableState.value.page,
      pageSize: tableState.value.pageSize,
      // The predetermined filter comes last so active filters can never override it.
      columnFilters: { ...buildColumnFilters(), usable: [String(props.usable)] },
    });
    files.value = result.results;
    totalCount.value = result.result_count;
    emit('update:count', result.result_count);
    // Cache each loaded row's revision→file uid so auto-tag can translate the
    // (revision-uid) selection into review-project file uids, even across pages.
    for (const row of result.results) {
      reviewProjectStore.dataRoomFileUidByRevisionUid[row.revision.uid] =
        row.review_project_file.uid;
      reviewProjectStore.dataRoomFileNameByRevisionUid[row.revision.uid] = row.revision.file_name;
    }
    sanitizeFilterOptions(result.filter_options);
  } catch (error) {
    logger.error('Error fetching review project files:', error);
    message.error('Failed to load files');
    files.value = [];
    totalCount.value = 0;
    emit('update:count', 0);
  } finally {
    loading.value = false;
  }
}

// The search field owns the debounce and fires this at once when it is cleared.
function applySearch(): void {
  tableState.value.page = 1;
  void fetchFiles();
}

// A chat-composer upload added a data-room file this list can't hear about (its
// websocket only covers rows already shown) — refetch; the list endpoint syncs the
// data room first, so the new file is in the response.
watch(
  () => reviewProjectStore.dataRoomFilesRefreshTick,
  () => void fetchFiles()
);

function handleFiltersUpdate(filters: Record<string, unknown>): void {
  tableState.value.activeFilters = filters;
  tableState.value.page = 1;
  void fetchFiles();
}

function handlePageChange(page: number): void {
  tableState.value.page = page;
  void fetchFiles();
}

function handleFileUpdated(updated: ReviewProjectFileInDb): void {
  const index = files.value.findIndex(f => f.review_project_file.uid === updated.uid);
  if (index !== -1) {
    files.value[index] = { ...files.value[index], review_project_file: updated };
  }
}

function rowByFileUid(fileUid: string): ReviewProjectFileWithRelations | undefined {
  return files.value.find(row => row.review_project_file.uid === fileUid);
}

// Server-side tag and seen-page changes (chat, auto-tag, other viewers) patch rows
// in place — mutating the reactive row keeps the open seen-detail modal live too.
// The tag filter's facet options only refresh on the next fetch, which is accepted.
useReviewProjectFileWebSocket({
  fileUids: () => files.value.map(row => row.review_project_file.uid),
  onFileUpdated: handleFileUpdated,
  onTagsUpdated: (fileUid, tags) => {
    const row = rowByFileUid(fileUid);
    if (row) row.review_project_file.tags = tags;
  },
  onSeenUpdated: (fileUid, pagesSeen) => {
    const row = rowByFileUid(fileUid);
    if (row) row.pages_seen = pagesSeen;
  },
  onStale: () => void fetchFiles(),
});

// Notes saved from the notes drawer (a separate component) — patch the row so the
// notes marker stays live without relying on a websocket echo to this client.
watch(
  () => reviewProjectStore.savedFileUpdate,
  updated => {
    if (updated) handleFileUpdated(updated);
  }
);

function fileName(row: ReviewProjectFileWithRelations): string {
  return row.revision.file_name;
}

/**
 * The row's status glyph. An unusable file failed to import; a usable one whose
 * preview hasn't finished is still being ingested.
 */
function rowStatus(row: ReviewProjectFileWithRelations): 'ready' | 'processing' | 'bad' {
  if (!props.usable) return 'bad';
  return row.revision.preview_status === PreprocessingTaskStatus.DONE ? 'ready' : 'processing';
}

/* -------------------------------------------------- fully-reviewed toggle */

const reviewedPendingUids = ref<Set<string>>(new Set());

async function toggleFullyReviewed(row: ReviewProjectFileWithRelations): Promise<void> {
  const file = row.review_project_file;
  if (reviewedPendingUids.value.has(file.uid)) return;
  reviewedPendingUids.value.add(file.uid);
  try {
    handleFileUpdated(
      await ReviewProjectService.updateFile(props.projectUid, file.uid, {
        fully_reviewed: !file.fully_reviewed,
      })
    );
    // The Statistics page's fully-reviewed count refetches on its next mount.
    reviewProjectStore.fileStatistics = null;
  } catch (error) {
    logger.error('Error updating review project file:', error);
    message.error('Failed to update file');
  } finally {
    reviewedPendingUids.value.delete(file.uid);
  }
}

/* -------------------------------------------------------------- row menu */

function previewReady(row: ReviewProjectFileWithRelations): boolean {
  return row.revision.preview_status === PreprocessingTaskStatus.DONE;
}

function hasNotes(row: ReviewProjectFileWithRelations): boolean {
  return (row.review_project_file.notes ?? '').trim().length > 0;
}

// Views carry a user, so a page can appear once per viewer — count distinct pages.
function distinctPagesSeen(file: ReviewProjectFileWithRelations): number {
  return new Set(file.pages_seen.map(view => view.page_number)).size;
}

function seenPercentage(row: ReviewProjectFileWithRelations): number {
  const pageCount = row.review_project_file.page_count;
  if (pageCount === 0) return 0;
  return Math.min(100, Math.round((distinctPagesSeen(row) / pageCount) * 100));
}

function menuIcon(icon: Record<string, unknown>): () => VNode {
  return () => h(NIcon, { size: 16 }, { default: () => h(icon) });
}

function rowMenuOptions(row: ReviewProjectFileWithRelations): DropdownOption[] {
  const options: DropdownOption[] = [];
  for (const key of props.actions) {
    if (key === 'preview') {
      options.push({
        key: 'preview',
        label: previewReady(row) ? 'Preview' : 'Preview not ready',
        icon: menuIcon(IconEye),
        disabled: !previewReady(row),
      });
    }
    if (key === 'notes') {
      options.push({
        key: 'notes',
        label: hasNotes(row) ? 'Notes ·' : 'Notes',
        icon: menuIcon(IconDocumentText),
      });
    }
  }
  if (shows.value.has('seen') && row.review_project_file.page_count > 0) {
    options.push({
      key: 'seen',
      label: `Pages seen · ${seenPercentage(row)}%`,
      icon: menuIcon(IconEye),
    });
  }
  return options;
}

function handleActionSelect(key: string, row: ReviewProjectFileWithRelations): void {
  if (key === 'preview' && previewReady(row)) {
    reviewProjectStore.showPreview(row.revision.uid);
  }
  if (key === 'notes') {
    reviewProjectStore.showNotes(
      {
        projectUid: props.projectUid,
        fileUid: row.review_project_file.uid,
        fileName: fileName(row),
      },
      row.review_project_file.notes ?? ''
    );
  }
  if (key === 'seen') {
    openSeenModal(row);
  }
}

/* ------------------------------------------------------------- selection */

const selectedUids = computed(() => reviewProjectStore.dataRoomSelectedRevisionUids);

function isChecked(row: ReviewProjectFileWithRelations): boolean {
  return selectedUids.value.includes(row.revision.uid);
}

function setChecked(row: ReviewProjectFileWithRelations, checked: boolean): void {
  const uid = row.revision.uid;
  const current = selectedUids.value;
  reviewProjectStore.dataRoomSelectedRevisionUids = checked
    ? [...current, uid]
    : current.filter(k => k !== uid);
}

const allChecked = computed(
  () => files.value.length > 0 && files.value.every(row => isChecked(row))
);
const someChecked = computed(() => files.value.some(row => isChecked(row)) && !allChecked.value);

function toggleAll(checked: boolean): void {
  const pageUids = files.value.map(row => row.revision.uid);
  if (checked) {
    reviewProjectStore.dataRoomSelectedRevisionUids = [
      ...new Set([...selectedUids.value, ...pageUids]),
    ];
  } else {
    const drop = new Set(pageUids);
    reviewProjectStore.dataRoomSelectedRevisionUids = selectedUids.value.filter(
      uid => !drop.has(uid)
    );
  }
}

const selectionLabel = computed(() =>
  selectedUids.value.length === 1
    ? '1 file selected'
    : `${selectedUids.value.length} files selected`
);

const countLabel = computed(() =>
  totalCount.value === 1 ? '1 file' : `${totalCount.value} files`
);

// The row the workspace is pointed at — the file whose preview is open.
function isSelectedRow(row: ReviewProjectFileWithRelations): boolean {
  return reviewProjectStore.previewTarget?.revisionUid === row.revision.uid;
}

// Clicking the row body opens it where it can be read; the checkbox is what
// builds a selection (that split is what the rail's bulk actions need).
function handleRowSelect(row: ReviewProjectFileWithRelations): void {
  if (!previewReady(row)) return;
  reviewProjectStore.showPreview(row.revision.uid);
}

/* ------------------------------------------------------------------ drag */

// Dragging a selected row drags the whole selection; entries for uids whose
// rows aren't on the current page stay uid-only (file names are cosmetic).
function dragEntryFor(uid: string): VaultFileDragEntry {
  const row = files.value.find(file => file.revision.uid === uid);
  return { revisionUid: uid, fileName: row?.revision.file_name };
}

function handleRowDragStart(event: DragEvent, row: ReviewProjectFileWithRelations): void {
  const uid = row.revision.uid;
  const uids = selectedUids.value.includes(uid) ? selectedUids.value : [uid];
  setVaultFileDragData(event, uids.map(dragEntryFor));
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy';
  }
}

/* ------------------------------------------------------- seen-pages modal */

const showSeenModal = ref(false);
const seenModalFile = ref<ReviewProjectFileWithRelations | null>(null);

function openSeenModal(row: ReviewProjectFileWithRelations): void {
  seenModalFile.value = row;
  showSeenModal.value = true;
}

const seenModalPercentage = computed((): number =>
  seenModalFile.value ? seenPercentage(seenModalFile.value) : 0
);

const seenModalPages = computed(() =>
  [...(seenModalFile.value?.pages_seen ?? [])].sort((a, b) => a.page_number - b.page_number)
);

function formatSeenAt(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const seenPagesColumns: DataTableColumns<ReviewProjectFilePageViewInDb> = [
  { title: 'Page', key: 'page_number', width: 80 },
  { title: 'Seen at', key: 'seen_at', render: row => formatSeenAt(row.seen_at) },
  {
    title: 'By',
    key: 'user',
    width: 56,
    align: 'center',
    render: row => h(UserInitialsAvatar, { user: row.user, size: 'xs' }),
  },
];

const success = gaiaStatus.success;

onMounted(fetchFiles);
</script>

<template>
  <div class="files-list">
    <div class="files-list__toolbar">
      <n-flex align="center" :size="6" :wrap="false">
        <gaia-search-input
          v-model:value="tableState.search"
          variant="fill"
          size="small"
          placeholder="Search files…"
          :debounce="500"
          @search="applySearch"
        />
        <n-tooltip placement="top" :delay="300">
          <template #trigger>
            <gaia-icon-button
              :dimension="30"
              :type="hasActiveFilters ? 'primary' : 'default'"
              aria-label="Filter files"
              @click="showFilterPanel = !showFilterPanel"
            >
              <n-icon :component="IconFilter" :size="14" />
            </gaia-icon-button>
          </template>
          Filter files
        </n-tooltip>
        <n-tooltip v-if="props.enableAutoTag" placement="top" :delay="300">
          <template #trigger>
            <gaia-icon-button
              :dimension="30"
              aria-label="Auto-tag files"
              @click="showAutoTagModal = true"
            >
              <n-icon :component="IconTags" :size="14" />
            </gaia-icon-button>
          </template>
          {{ autoTagButtonLabel }}
        </n-tooltip>
      </n-flex>

      <gaia-filter-panel
        v-model:visible="showFilterPanel"
        :filter-options="filterOptions"
        :active-filters="tableState.activeFilters"
        :filter-config="filterConfig"
        @update:filters="handleFiltersUpdate"
      />
    </div>

    <!-- Select-all sits above the rows and indented to their checkbox column, so
         the control lines up with what it ticks — and the bulk actions it enables
         come with it rather than waiting at the far end of the pane. -->
    <div class="files-list__selection">
      <gaia-checkbox
        :checked="allChecked"
        :indeterminate="someChecked"
        size="small"
        aria-label="Select all files on this page"
        @update:checked="toggleAll"
      />
      <gaia-text v-if="selectedUids.length === 0" size="micro" tone="subtle">
        {{ countLabel }}
      </gaia-text>
      <template v-else>
        <gaia-text size="micro" weight="semibold" tone="ink">{{ selectionLabel }}</gaia-text>
        <gaia-button
          v-if="props.enableAutoTag"
          text
          size="tiny"
          :theme-overrides="gaiaTextButtonOverrides.ink"
          @click="showAutoTagModal = true"
        >
          Add tags
        </gaia-button>
        <gaia-button
          text
          size="tiny"
          :theme-overrides="gaiaTextButtonOverrides.ink"
          @click="reviewProjectStore.dataRoomSelectedRevisionUids = []"
        >
          Clear
        </gaia-button>
      </template>
    </div>

    <div class="files-list__rows">
      <gaia-spinner v-if="loading && files.length === 0" :style="{ margin: '32px auto' }" />

      <gaia-empty-state
        v-else-if="files.length === 0"
        dashed
        :title="tableState.search ? 'No files match' : 'No files yet'"
      >
        {{
          tableState.search
            ? 'Try a shorter term, or clear the search.'
            : 'Add files to the data room to review them here.'
        }}
      </gaia-empty-state>

      <gaia-file-row
        v-for="row in files"
        :key="row.revision.uid"
        :name="fileName(row)"
        :path="shows.has('path') ? row.review_project_file.path : undefined"
        :version="shows.has('version') ? `v${row.review_project_file.version_number}` : undefined"
        :status="rowStatus(row)"
        :selected="isSelectedRow(row)"
        :checked="isChecked(row)"
        :draggable="props.draggableRows"
        @select="handleRowSelect(row)"
        @update:checked="checked => setChecked(row, checked)"
        @dragstart="(event: DragEvent) => handleRowDragStart(event, row)"
      >
        <template v-if="shows.has('tags')" #tags>
          <review-file-tags-cell
            :project-uid="props.projectUid"
            :file="row.review_project_file"
            @updated="handleFileUpdated"
          />
        </template>

        <template v-if="shows.has('reviewed') || shows.has('actions')" #trailing>
          <n-flex align="center" :size="0" :wrap="false">
            <n-tooltip v-if="shows.has('reviewed')" placement="top" :delay="300">
              <template #trigger>
                <gaia-icon-button
                  :dimension="26"
                  :loading="reviewedPendingUids.has(row.review_project_file.uid)"
                  :aria-label="
                    row.review_project_file.fully_reviewed
                      ? 'Mark as not fully reviewed'
                      : 'Mark as fully reviewed'
                  "
                  @click="toggleFullyReviewed(row)"
                >
                  <n-icon
                    :component="
                      row.review_project_file.fully_reviewed
                        ? IconCheckCircleFilled
                        : IconCheckCircle
                    "
                    :size="16"
                    :color="row.review_project_file.fully_reviewed ? success : undefined"
                  />
                </gaia-icon-button>
              </template>
              {{
                row.review_project_file.fully_reviewed
                  ? 'Mark as not fully reviewed'
                  : 'Mark as fully reviewed'
              }}
            </n-tooltip>

            <gaia-dropdown
              v-if="shows.has('actions') && rowMenuOptions(row).length > 0"
              :options="rowMenuOptions(row)"
              placement="bottom-end"
              @select="key => handleActionSelect(key, row)"
            >
              <gaia-icon-button :dimension="26" aria-label="File actions">
                <n-icon :component="IconMore" :size="16" />
              </gaia-icon-button>
            </gaia-dropdown>
          </n-flex>
        </template>
      </gaia-file-row>
    </div>

    <footer v-if="totalCount > tableState.pageSize" class="files-list__footer">
      <gaia-pagination
        :page="tableState.page"
        :page-size="tableState.pageSize"
        :total="totalCount"
        :page-sizes="[]"
        @update:page="handlePageChange"
      />
    </footer>
  </div>

  <gaia-modal
    v-model:show="showSeenModal"
    width="480px"
    :title="seenModalFile ? fileName(seenModalFile) : ''"
  >
    <n-flex v-if="seenModalFile" vertical :size="12">
      <gaia-text>
        {{ distinctPagesSeen(seenModalFile) }} of
        {{ seenModalFile.review_project_file.page_count }} pages seen
      </gaia-text>
      <gaia-progress-bar :percentage="seenModalPercentage" />
      <gaia-table
        v-if="seenModalPages.length > 0"
        size="small"
        :columns="seenPagesColumns"
        :data="seenModalPages"
        :max-height="320"
        :row-key="(row: ReviewProjectFilePageViewInDb) => `${row.page_number}-${row.user.uid}`"
      />
      <gaia-empty-state v-else dashed title="No pages seen yet">
        Opening the file in the preview records the pages the team has read.
      </gaia-empty-state>
    </n-flex>
  </gaia-modal>

  <auto-tag-modal
    v-if="props.enableAutoTag"
    v-model:show="showAutoTagModal"
    :project-uid="props.projectUid"
    :selected-file-uids="selectedFileUids"
    @created="onAutoTagCreated"
  />
</template>

<style scoped>
.files-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* The pane's own toolbar and footer strips, drawn here rather than in the
   pane's #toolbar / #footer slots: this list is mounted in the pane's body by
   ReviewDataRoom, which switches between three views and so cannot lift one
   view's controls into its own slots. Reading the geometry off the pane's
   custom properties keeps the two aligned anyway — the numbers have one
   definition (GaiaWorkspacePane), not two that drift. */
.files-list__toolbar {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: var(--gaia-pane-toolbar-padding);
}

/* Left padding matches a row's checkbox: the scroller's 12px plus a row's own 8px. */
.files-list__selection {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 var(--gaia-pane-inset) 8px;
}

/* The rail's own scroller — the rows are the only thing in the pane that scrolls. */
.files-list__rows {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 12px 14px;
}

/* Paging only, and only when there is more than one page to reach. */
.files-list__footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: var(--gaia-pane-footer-padding);
  border-top: var(--gaia-pane-rule);
}
</style>
